import {getTestProps} from '@benchling/js-test-utils';
import React, {useCallback} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {DEEP_RESEARCH_ANALYTICS_ACTIONS} from 'constants/analytics-products/DeepResearchAnalyticsConstants';
import {getActiveWorkspaceEntry, openAddToEntryModal} from 'llm/modals/AddToEntryModal';
import {ChartNotePart} from 'notebook/integrations/charts/NoteLinkedChartPlugin';
import {CHART_CID_PREFIX} from 'notebook/integrations/charts/NoteLinkedChartUtil';
import {makeDefaultItem} from 'notebook/notes/CreateOps';
import NoteConstants from 'notebook/notes/NoteConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import uniqueId from 'util/uniqueId';

/**
 * Builds a `note_linked_chart` note part for a chart produced in a chat (e.g. Deep
 * Research). The backend receives a new chart cid plus `source_id` and clones the
 * source chart into the target entry on first save.
 */
export function createChartNotePart(chartId: string): ChartNotePart {
  return {
    ...makeDefaultItem(NoteConstants.NOTE_LINKED_OBJECT, ''),
    item: {
      cid: uniqueId('note-linked-object-'),
      type: NoteLinkedObjectType.NOTE_LINKED_CHART,
      name: 'AI Chart',
      chart: {
        id: null,
        source_id: chartId,
        cid: uniqueId(CHART_CID_PREFIX),
      },
      provenance: {url: ''},
    },
  } as ChartNotePart;
}

interface SaveChartToEntryButtonProps {
  chartId: string;
  chatSessionId?: string;
}

/**
 * Renders a button overlaid on a chart in a chat that lets the user add that single chart
 * into an existing ELN entry as a `note_linked_chart` note part.
 */
export function SaveChartToEntryButton({chartId, chatSessionId}: SaveChartToEntryButtonProps): JSX.Element {
  const handleClick = useCallback(async () => {
    trackEvent(DEEP_RESEARCH_ANALYTICS_ACTIONS.SAVED_DEEP_RESEARCH_TO_ENTRY, {
      targetType: 'single_chart',
    });
    const result = await openAddToEntryModal({
      notePartsContentSourceId: chatSessionId,
      getContentsToInsert: () => Promise.resolve({noteParts: [createChartNotePart(chartId)]}),
      initialSelectedEntry: getActiveWorkspaceEntry(),
    });
    if (result) {
      notifications.success('Chart has been added to the selected entry! Navigating to entry...');
    }
  }, [chartId, chatSessionId]);

  return (
    <Button
      buttonStyle={ButtonStyle.TERTIARY_GRAY}
      size={ButtonSize.MINI}
      icon="plus"
      text="Add to entry"
      onClick={handleClick}
      {...getTestProps({
        component: 'SaveChartToEntryButton',
        element: 'addToEntryButton',
        key: chartId,
      })}
      data-amp-safe={false}
    />
  );
}
